import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ExtensionIcon from '@mui/icons-material/Extension';
import ForumIcon from '@mui/icons-material/Forum';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import styled from '@mui/material/styles/styled';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useUser } from '../../../contexts/UserContext';
import { PUBLIC_ROUTE, PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import Dropdown from '../../elements/dropdown/Dropdown';
import Image from '../../elements/image/Image';
import Logo from '../logo/Logo';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const PrivateHeader = ({ open, handleLogout, isAdmin }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('titles');
  const { projectId } = useParams();
  const { user } = useUser();

  const [createAnchor, setCreateAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);

  const toggleCreateDropdown = (e) =>
    createAnchor ? setCreateAnchor(null) : setCreateAnchor(e.currentTarget);

  const toggleUserDropdown = (e) =>
    userAnchor ? setUserAnchor(null) : setUserAnchor(e.currentTarget);

  const routeTo = (path, toggleFunc) => {
    toggleFunc?.();
    navigate(path);
  };

  const isJaLocale = user?.locale === 'ja';

  const createDropdown =
    projectId && isAdmin
      ? [
          {
            name: t('newProject'),
            handleClick: () =>
              routeTo(PRIVATE_ROUTE.PROJECT_NEW, toggleCreateDropdown),
          },
          {
            name: t('newInstance'),
            handleClick: () =>
              routeTo(
                buildPath({
                  path: PRIVATE_ROUTE.INSTANCE_NEW,
                  params: { projectId },
                }),
                toggleCreateDropdown
              ),
          },
        ]
      : [
          {
            name: t('newProject'),
            handleClick: () =>
              routeTo(PRIVATE_ROUTE.PROJECT_NEW, toggleCreateDropdown),
          },
        ];

  const userDropdown = [
    {
      name: t('profile'),
      handleClick: () =>
        routeTo(PRIVATE_ROUTE.USER_PROFILE, toggleUserDropdown),
    },
    {
      name: t('account'),
      handleClick: () =>
        routeTo(PRIVATE_ROUTE.USER_ACCOUNT, toggleUserDropdown),
    },
    {
      name: t('logout'),
      handleClick: handleLogout,
    },
  ];

  return (
    <AppBar elevation={3} position="fixed">
      <Toolbar disableGutters>
        <Logo open={open} />
        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={t('forum')}>
          <StyledIconButton
            color="inherit"
            target="_blank"
            href={isJaLocale ? PUBLIC_ROUTE.FORUM : PUBLIC_ROUTE.EN_FORUM}
          >
            <ForumIcon />
          </StyledIconButton>
        </Tooltip>

        <Tooltip title={t('docs')}>
          <StyledIconButton
            color="inherit"
            target="_blank"
            href={
              isJaLocale
                ? PUBLIC_ROUTE.DOCUMENTATION
                : PUBLIC_ROUTE.EN_DOCUMENTATION
            }
          >
            <LibraryBooksIcon />
          </StyledIconButton>
        </Tooltip>

        <Tooltip title={t('extensionLibrary')}>
          <StyledIconButton
            color="inherit"
            aria-label="go to extension page"
            onClick={() =>
              routeTo(
                buildPath({
                  path: PRIVATE_ROUTE.EXTENSIONS,
                })
              )
            }
          >
            <ExtensionIcon />
          </StyledIconButton>
        </Tooltip>

        <StyledIconButton
          edge="end"
          color="inherit"
          aria-label="create-menu"
          onClick={toggleCreateDropdown}
        >
          <AddIcon />
        </StyledIconButton>

        <Dropdown
          id="create-menu"
          anchorEl={createAnchor}
          onClose={toggleCreateDropdown}
        >
          {createDropdown.map(({ name, handleClick }) => (
            <MenuItem key={name} onClick={handleClick}>
              {name}
            </MenuItem>
          ))}
        </Dropdown>
        <IconButton
          aria-label="user-menu"
          sx={{ marginLeft: 2, marginRight: 4, padding: 0 }}
          onClick={toggleUserDropdown}
          size="large"
        >
          <Image alt={`avatar-${user?.initials}`} src={user?.icon}>
            {user?.initials}
          </Image>
        </IconButton>

        <Dropdown
          id="user-menu"
          anchorEl={userAnchor}
          onClose={toggleUserDropdown}
        >
          {userDropdown.map(({ name, handleClick }, i) =>
            i === userDropdown.length - 1 ? (
              <div key={name}>
                <Divider />
                <MenuItem aria-label="log out" onClick={handleClick}>
                  {name}
                </MenuItem>
              </div>
            ) : (
              <MenuItem key={name} onClick={handleClick}>
                {name}
              </MenuItem>
            )
          )}
        </Dropdown>
      </Toolbar>
    </AppBar>
  );
};

PrivateHeader.propTypes = {
  open: PropTypes.bool,
  fullWidth: PropTypes.bool,
  handleLogout: PropTypes.func,
  isAdmin: PropTypes.bool,
};

export default PrivateHeader;
